import {
  Outlet,
  createFileRoute,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { ErrorDisplay } from '@/components/common/error-display'
import { UserProfileHeader } from '@/features/users'
import type { DetailedUser } from '@/features/users'
import { useDirectReportProfile } from '@/features/users/services/users.convex'
import { AuthGuard } from '@/features/auth/components/auth-guard'
import { useCurrentOrganizationId, usePermissions } from '@/features/auth'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'

export const Route = createFileRoute(
  '/(dashboard)/direct-reports/$directReportId',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const currentOrgId = useCurrentOrganizationId()
  const { directReportId } = Route.useParams()
  const { data, isLoading, error } = useDirectReportProfile(directReportId)
  const navigate = useNavigate()
  const { location } = useRouterState()
  const { hasOrganizationRole } = usePermissions()
  const isPrivilegedView = currentOrgId
    ? hasOrganizationRole(currentOrgId, 'admin') ||
      hasOrganizationRole(currentOrgId, 'manager')
    : false

  function getTabFromRoute(pathname: string): string | undefined {
    if (pathname.includes('/career-path')) {
      return 'career-path'
    }
    if (pathname.includes('/assessments')) {
      return 'assessments'
    }
    if (pathname.includes('/user-settings')) {
      return 'user-settings'
    }
    return undefined
  }

  const [tab, setTab] = useState<string | undefined>(() =>
    getTabFromRoute(location.pathname),
  )

  useEffect(() => {
    const currentTab = getTabFromRoute(location.pathname)
    setTab(currentTab)
  }, [location.pathname])

  // Redirect members away from restricted tabs
  useEffect(() => {
    if (!isPrivilegedView && tab && tab !== 'career-path') {
      navigate({
        to: `/direct-reports/${directReportId}/career-path/`,
        search: { name: data?.name || undefined },
      })
    }
  }, [isPrivilegedView, tab, directReportId, data?.name, navigate])

  function showCareerPath() {
    navigate({
      to: `/direct-reports/${directReportId}/career-path/`,
      search: { name: data?.name || undefined },
    })
  }

  function showAssessments() {
    navigate({
      to: `/direct-reports/${directReportId}/assessments/`,
      search: { name: data?.name || undefined },
    })
  }

  function showUserSettings() {
    navigate({
      to: `/direct-reports/${directReportId}/user-settings/`,
      search: { name: data?.name || undefined },
    })
  }

  function changeTab(newTab: string) {
    setTab(newTab)
    if (newTab === 'career-path') {
      showCareerPath()
    } else if (newTab === 'assessments') {
      showAssessments()
    } else if (newTab === 'user-settings') {
      showUserSettings()
    } else {
      showCareerPath()
    }
  }

  if (isLoading) {
    return (
      <AuthGuard
        requiredOrganizationRole={{
          organizationId: currentOrgId || '',
          role: ['admin', 'manager', 'member', 'analytics', 'billing'],
        }}
      >
        <>
          {/* Header Skeleton */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6 bg-muted/30 rounded-lg">
            {/* Left side: Avatar and Info */}
            <div className="flex items-start gap-4">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-4 w-96" />
              </div>
            </div>

            {/* Right side: Action Buttons (privileged only) */}
            {isPrivilegedView && (
              <div className="flex flex-wrap items-center gap-2">
                <Skeleton className="h-9 w-32" />
                <Skeleton className="h-9 w-32" />
                <Skeleton className="h-9 w-24" />
                <Skeleton className="h-9 w-20" />
              </div>
            )}
          </div>

          {/* Tabs Skeleton (privileged only) */}
          {isPrivilegedView && (
            <div className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground w-fit">
              <div className="h-8 w-28 rounded-md bg-background/60 animate-pulse" />
              <div className="h-8 w-28 rounded-md bg-background/60 animate-pulse ml-1" />
            </div>
          )}
          <div className="p-6 bg-muted/30 rounded-lg">
            <Skeleton className="h-48 w-full" />
          </div>
        </>
      </AuthGuard>
    )
  }

  if (error) {
    return <ErrorDisplay error={error} title="Error loading user" />
  }

  return (
    <AuthGuard
      requiredOrganizationRole={{
        organizationId: currentOrgId || '',
        role: ['admin', 'manager', 'member', 'analytics', 'billing'],
      }}
    >
      {data ? (
        <>
          <UserProfileHeader
            user={
              {
                id: data.id,
                name: data.name,
                email: data.email,
                avatar: data.avatar,
                currentUserPosition: data.position
                  ? {
                      sinceDate: data.position.sinceDate,
                      positionTrajectory: {
                        fromPosition: {
                          id: data.position.id,
                          title: data.position.title,
                          level: data.position.level,
                          role: data.position.role,
                          description: data.position.description,
                        },
                      },
                    }
                  : null,
                manager: data.manager
                  ? { id: data.manager.id, name: data.manager.name ?? '' }
                  : null,
                organization: data.organization ?? null,
              } as unknown as DetailedUser
            }
            showActions={isPrivilegedView}
            onPromote={() =>
              toast.success(`${data.name} has been successfully promoted!`)
            }
          />
          {isPrivilegedView ? (
            <Tabs value={tab} onValueChange={changeTab}>
              <TabsList className="grid w-full max-w-md grid-cols-3">
                <TabsTrigger value="career-path">Career Path</TabsTrigger>
                <TabsTrigger value="assessments">Assessments</TabsTrigger>
                <TabsTrigger value="user-settings">User Settings</TabsTrigger>
              </TabsList>
              {tab && (
                <TabsContent value={tab}>
                  <Outlet />
                </TabsContent>
              )}
            </Tabs>
          ) : (
            <Outlet />
          )}
        </>
      ) : (
        <div>No data available</div>
      )}
    </AuthGuard>
  )
}
